import axios from 'axios';
import * as Yup from 'yup';

import { DOCUMENT_NAME_MAX_SIZE } from '../DocumentCard';

interface UploadStoredFileParams {
  urlPrefix: string;
  file: File;
  onUpdatePercent: (progressEvent: ProgressEvent) => void;
}

export const uploadStoredFile = async ({
  urlPrefix,
  file,
  onUpdatePercent,
}: UploadStoredFileParams): Promise<string | undefined> => {
  const { data } = await axios.post(`${urlPrefix}/upload_url`, {
    file_size: file.size,
    file_type: file.type,
    file_name: file.name,
  });

  const uploadUrl = data.upload_url;
  const storageFileName = data.storage_filename;

  const config = {
    onUploadProgress: onUpdatePercent,
  };

  await axios.put(uploadUrl, file, config);
  return storageFileName;
};

export const fileNameToDocumentName = (fileName: string): string => {
  return fileName.replace(/\.[^/.]+$/, '');
};

interface DocumentFieldsValidationOptions {
  isClaimIdRequired?: boolean;
  includeFiles?: boolean;
}

/**
 * Shared validation schema for document fields used across upload components
 * @param options - Configuration options for which fields to include
 * @returns Yup validation schema object
 */
export const documentFieldsValidation = (options: DocumentFieldsValidationOptions = {}) => {
  const { isClaimIdRequired = true, includeFiles = false } = options;

  return {
    ...(isClaimIdRequired !== undefined && {
      claim_id: isClaimIdRequired ? Yup.number().required() : Yup.number(),
    }),
    document_name: Yup.string().nullable().max(DOCUMENT_NAME_MAX_SIZE),
    document_date: Yup.date().required('Required'),
    document_date_received: Yup.date()
      .nullable()
      .when('document_date', {
        is: (val: string) => !!val,
        then: Yup.date().nullable().min(Yup.ref('document_date'), "Can't be before document creation date"),
      }),
    exposure_ids: Yup.array().required('Required').min(1, 'Required'),
    type: Yup.string().required('Required'),
    summary: Yup.string(),
    ...(includeFiles && {
      files: Yup.array()
        .required('Required')
        .min(1, 'Required')
        .test('no-failed-uploads', 'Please remove all failed upload documents', (files) => {
          return files ? !files.some((file) => file.isError === true) : true;
        }),
    }),
  };
};
